import { useCallback, useEffect, useState } from 'react'
import type { CSSProperties } from 'react'
import { useNavigate } from 'react-router-dom'
import LoadingPlaceholder from '../components/LoadingPlaceholder'
import EmptyState from '../components/EmptyState'
import RecapTileGrid from '../components/RecapTileGrid'
import { formatUtcDateTime } from '../utils/timezone'
import { getSessionAuth } from '../utils/sessionAuth'
import { listArchivedConversations, listArchivedClients, listArchivedActionItems, listArchivedCalendarEvents, archiveConversation, archiveClient, archiveActionItem, archiveCalendarServiceEvent, deleteConversation, deleteClient, deleteActionItem, deleteCalendarServiceEvent } from '../api/crm'
import { listArchivedChats, unarchiveChat, deleteChat } from '../api/chat'
import { ensureSessionUserId, getSessionUserId } from '../utils/sessionUser'
import '../style/views/ArchivesView.css'

type Conversation = {
  id: string
  title?: string
  date_created?: string
  datetime_start?: string
  datetime_end?: string
  type?: 'CALL' | 'CHAT'
}

type ArchiveType = 'Conversations' | 'Chats' | 'Contacts' | 'Calendar' | 'Action Items'

const archiveFilters: ArchiveType[] = ['Conversations', 'Chats', 'Contacts', 'Calendar', 'Action Items']

function ArchivesView() {
  const navigate = useNavigate()
  const [items, setItems] = useState<Conversation[]>([])
  const [loading, setLoading] = useState(true)
  const [userId, setUserId] = useState<string | null>(() => getSessionUserId())
  const [activeType, setActiveType] = useState<ArchiveType>('Conversations')
  const [actionError, setActionError] = useState<string | null>(null)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [unarchivingSelected, setUnarchivingSelected] = useState(false)
  const [deletingSelected, setDeletingSelected] = useState(false)
  const archiveActiveIndex = Math.max(0, archiveFilters.indexOf(activeType))

  useEffect(() => {
    const controller = new AbortController()
    const loadArchived = async () => {
      const ensuredId = userId || (await ensureSessionUserId(getSessionAuth()))
      if (!ensuredId) {
        setLoading(false)
        return
      }
      if (!userId) setUserId(ensuredId)
      try {
        setLoading(true)
        let raw: unknown[]
        if (activeType === 'Conversations') {
          raw = await listArchivedConversations(ensuredId, controller.signal)
        } else if (activeType === 'Chats') {
          raw = await listArchivedChats(ensuredId, controller.signal)
        } else if (activeType === 'Contacts') {
          raw = await listArchivedClients(ensuredId, controller.signal)
        } else if (activeType === 'Action Items') {
          raw = await listArchivedActionItems(ensuredId, controller.signal)
        } else {
          raw = await listArchivedCalendarEvents(ensuredId, controller.signal)
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO(ENG-399): replace with proper type
        const normalized: Conversation[] = raw.map((item: any) => {
          if (activeType === 'Contacts') {
            const title = [item.first_name, item.last_name].filter(Boolean).join(' ').trim() || item.email || 'Contact'
            return { id: item.id, title, date_created: item.date_created || item.datetime_start || item.created_at }
          }
          if (activeType === 'Action Items') {
            const title = (item.item || '').trim() || 'Untitled action item'
            return { id: item.id, title, date_created: item.date_created || item.created_at }
          }
          if (activeType === 'Calendar') {
            return {
              id: item.id,
              title: item.title,
              date_created: item.datetime_start || item.date_created,
              datetime_start: item.datetime_start,
              datetime_end: item.datetime_end,
            }
          }
          const itemType = activeType === 'Chats' ? 'CHAT' : 'CALL'
          return { id: item.id, title: item.title, date_created: item.date_created, type: itemType }
        })
        setItems(normalized)
      } catch (err) {
        if (!controller.signal.aborted) {
          console.error('Failed to load archived items', err)
          setItems([])
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false)
      }
    }
    loadArchived()
    return () => controller.abort()
  }, [activeType, userId])

  useEffect(() => {
    setSelectedIds(new Set())
    setActionError(null)
  }, [activeType])

  const handleSelect = (id: string) => {
    if (activeType === 'Contacts') {
      navigate(`/contact/${id}`)
    } else if (activeType === 'Calendar') {
      navigate(`/calendar?focus=${id}`)
    } else if (activeType === 'Action Items') {
      return
    } else if (activeType === 'Chats') {
      navigate(`/assistant?id=${id}`)
    } else {
      navigate(`/conversation/${id}`)
    }
  }

  const headerTitle = 'Archived items'

  const handleToggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }, [])

  const handleClearSelection = useCallback(() => {
    setSelectedIds(new Set())
  }, [])

  const handleUnarchiveSelected = useCallback(async () => {
    if (!selectedIds.size || unarchivingSelected) return
    const label = activeType === 'Contacts' ? 'contact' : activeType === 'Calendar' ? 'event' : activeType === 'Action Items' ? 'action item' : 'item'
    const confirmed = window.confirm(
      `Unarchive ${selectedIds.size} ${label}${selectedIds.size === 1 ? '' : 's'}?`,
    )
    if (!confirmed) return
    try {
      setUnarchivingSelected(true)
      setActionError(null)
      const ensuredId = userId || (await ensureSessionUserId(getSessionAuth()))
      if (!ensuredId) throw new Error('Missing user id')
      
      const results = await Promise.all(
        Array.from(selectedIds).map(async (id) => {
          try {
            if (activeType === 'Contacts') {
              await archiveClient(id, ensuredId)
            } else if (activeType === 'Chats') {
              await unarchiveChat(id, ensuredId)
            } else if (activeType === 'Calendar') {
              await archiveCalendarServiceEvent(id, ensuredId)
            } else if (activeType === 'Action Items') {
              await archiveActionItem(id, ensuredId)
            } else {
              await archiveConversation(id, ensuredId)
            }
            return { id, ok: true }
          } catch (error) {
            console.error('Failed to unarchive item', error)
            return { id, ok: false }
          }
        }),
      )
      const succeeded = new Set(results.flatMap((r) => r.ok ? [r.id] : []))
      const failed = results.flatMap((r) => r.ok ? [] : [r.id])
      if (succeeded.size) {
        setItems((prev) => prev.filter((item) => !succeeded.has(item.id)))
        setSelectedIds((prev) => {
          const next = new Set(prev)
          succeeded.forEach((id) => next.delete(id))
          return next
        })
      }
      if (failed.length) {
        setActionError(`Unable to unarchive ${failed.length} ${label}${failed.length === 1 ? '' : 's'}.`)
      }
    } catch (error) {
      console.error('Failed to unarchive selected items', error)
      setActionError(error instanceof Error ? error.message : 'Unable to unarchive selected items.')
    } finally {
      setUnarchivingSelected(false)
    }
  }, [activeType, selectedIds, unarchivingSelected, userId])

  const handleDeleteSelected = useCallback(async () => {
    if (activeType === 'Calendar' || !selectedIds.size || deletingSelected) return
    const label = activeType === 'Contacts' ? 'contact' : activeType === 'Action Items' ? 'action item' : 'call'
    const confirmed = window.confirm(
      `Delete ${selectedIds.size} ${label}${selectedIds.size === 1 ? '' : 's'} permanently? This cannot be undone.`,
    )
    if (!confirmed) return
    try {
      setDeletingSelected(true)
      setActionError(null)
      const ensuredId = userId || (await ensureSessionUserId(getSessionAuth()))
      if (!ensuredId) throw new Error('Missing user id')
      const deleteResults = await Promise.all(
        Array.from(selectedIds).map(async (id) => {
          try {
            if (activeType === 'Contacts') {
              await deleteClient(id, ensuredId)
            } else if (activeType === 'Action Items') {
              await deleteActionItem(id, ensuredId)
            } else if (activeType === 'Chats') {
              await deleteChat(id, ensuredId)
            } else {
              await deleteConversation(id, ensuredId)
            }
            return { id, ok: true }
          } catch (error) {
            console.error('Failed to delete item', error)
            return { id, ok: false }
          }
        }),
      )
      const succeeded = new Set(deleteResults.flatMap((r) => r.ok ? [r.id] : []))
      const failed = deleteResults.flatMap((r) => r.ok ? [] : [r.id])
      if (succeeded.size) {
        setItems((prev) => prev.filter((item) => !succeeded.has(item.id)))
        setSelectedIds((prev) => {
          const next = new Set(prev)
          succeeded.forEach((id) => next.delete(id))
          return next
        })
      }
      if (failed.length) {
        setActionError(`Unable to delete ${failed.length} ${label}${failed.length === 1 ? '' : 's'}.`)
      }
    } catch (error) {
      console.error('Failed to delete selected items', error)
      setActionError(error instanceof Error ? error.message : 'Unable to delete selected items.')
    } finally {
      setDeletingSelected(false)
    }
  }, [activeType, deletingSelected, selectedIds, userId])

  const handleUnarchiveCalendar = async (id: string) => {
    try {
      setActionError(null)
      const ensuredId = userId || (await ensureSessionUserId(getSessionAuth()))
      if (!ensuredId) throw new Error('Missing user id')
      await archiveCalendarServiceEvent(id, ensuredId)
      setItems((prev) => prev.filter((item) => item.id !== id))
    } catch (err) {
      console.error('Failed to unarchive calendar event', err)
      setActionError(err instanceof Error ? err.message : 'Unable to unarchive right now.')
    }
  }

  const handleDeleteCalendar = async (id: string) => {
    try {
      const confirmed = window.confirm('Delete this event permanently? This cannot be undone.')
      if (!confirmed) return
      setActionError(null)
      const ensuredId = userId || (await ensureSessionUserId(getSessionAuth()))
      if (!ensuredId) throw new Error('Missing user id')
      await deleteCalendarServiceEvent(id, ensuredId)
      setItems((prev) => prev.filter((item) => item.id !== id))
    } catch (err) {
      console.error('Failed to delete calendar event', err)
      setActionError(err instanceof Error ? err.message : 'Unable to delete event.')
    }
  }

  const handleUnarchiveActionItem = async (id: string) => {
    try {
      setActionError(null)
      const ensuredId = userId || (await ensureSessionUserId(getSessionAuth()))
      if (!ensuredId) throw new Error('Missing user id')
      await archiveActionItem(id, ensuredId)
      setItems((prev) => prev.filter((item) => item.id !== id))
    } catch (err) {
      console.error('Failed to unarchive action item', err)
      setActionError(err instanceof Error ? err.message : 'Unable to unarchive right now.')
    }
  }

  const handleDeleteActionItem = async (id: string) => {
    try {
      const confirmed = window.confirm('Delete this action item permanently? This cannot be undone.')
      if (!confirmed) return
      setActionError(null)
      const ensuredId = userId || (await ensureSessionUserId(getSessionAuth()))
      if (!ensuredId) throw new Error('Missing user id')
      await deleteActionItem(id, ensuredId)
      setItems((prev) => prev.filter((item) => item.id !== id))
    } catch (err) {
      console.error('Failed to delete action item', err)
      setActionError(err instanceof Error ? err.message : 'Unable to delete action item.')
    }
  }

  return (
    <>
      <div className="content-header header-title-bar">
        <div>
          <p className="eyebrow" style={{ marginBottom: '2px' }}>
            Archives
          </p>
          <h1 style={{ margin: 0 }}>{headerTitle}</h1>
        </div>
        <div className="content-header-actions">
          {selectedIds.size ? (
            <>
              <button
                type="button"
                className="icon-button info with-label header-action-button"
                onClick={handleUnarchiveSelected}
                disabled={unarchivingSelected || deletingSelected}
              >
                {unarchivingSelected ? 'Unarchiving...' : '↺ Unarchive'}
              </button>
              <button
                type="button"
                className="danger-button header-action-button"
                onClick={handleDeleteSelected}
                disabled={unarchivingSelected || deletingSelected}
              >
                {deletingSelected ? 'Deleting...' : 'Delete'}
              </button>
              <button
                type="button"
                className="ghost-button header-action-button"
                onClick={handleClearSelection}
                disabled={unarchivingSelected || deletingSelected}
              >
                Cancel
              </button>
            </>
          ) : null}
          <div
            className="filters"
            style={
              {
                ['--active-index' as string]: archiveActiveIndex,
                ['--filter-count' as string]: archiveFilters.length,
              } as CSSProperties
            }
          >
            {archiveFilters.map((type) => (
              <button
                type="button"
                key={type}
                className={`filter ${activeType === type ? 'active' : ''}`}
                onClick={() => setActiveType(type)}
              >
                {type}
              </button>
            ))}
          </div>
        </div>
        <button type="button" className="ghost-button" onClick={() => navigate('/settings')}>
          ← Back to settings
        </button>
      </div>
      <div className="content-panel">
        {actionError && activeType !== 'Calendar' && activeType !== 'Action Items' && (
          <div className="muted archive-error">{actionError}</div>
        )}
        {loading ? (
          <LoadingPlaceholder variant="all" />
        ) : items.length > 0 ? (
          activeType === 'Calendar' ? (
            <div className="stack">
              {actionError && (
                <div className="muted" style={{ color: '#b42318', marginBottom: '8px' }}>
                  {actionError}
                </div>
              )}
              <div className="tiles-grid">
                {items.map((item) => {
                  const start = formatUtcDateTime(item.datetime_start)
                  const end = formatUtcDateTime(item.datetime_end)
                  const title = (item.title || '').trim() || 'Untitled event'
                  return (
                    <div
                      key={item.id}
                      className="tile data-tile archived-calendar-tile"
                    >
                      <div className="tile-meta">
                        <span className="tile-pill">{start.date}</span>
                        <span className="tile-pill muted">
                          {start.time}{start.time && end.time ? ' – ' : ''}{end.time || '—'}
                        </span>
                      </div>
                      <div className="tile-title" title={title}>
                        {title}
                      </div>
                      <div className="archive-action" style={{ display: 'flex', gap: '6px' }}>
                        <button
                          type="button"
                          className="icon-button info small"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleUnarchiveCalendar(item.id)
                          }}
                        >
                          ↺
                        </button>
                        <button
                          type="button"
                          className="danger-button small"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleDeleteCalendar(item.id)
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          ) : activeType === 'Action Items' ? (
            <div className="stack">
              {actionError && (
                <div className="muted" style={{ color: '#b42318', marginBottom: '8px' }}>
                  {actionError}
                </div>
              )}
              <div className="tiles-grid">
                {items.map((item) => {
                  const created = formatUtcDateTime(item.date_created)
                  const title = (item.title || '').trim() || 'Untitled action item'
                  return (
                    <div
                      key={item.id}
                      className="tile data-tile archived-calendar-tile"
                    >
                      <div className="tile-meta">
                        <span className="tile-pill">{created.date}</span>
                        <span className="tile-pill muted">{created.time || '—'}</span>
                      </div>
                      <div className="tile-title" title={title}>
                        {title}
                      </div>
                      <div className="archive-action" style={{ display: 'flex', gap: '6px' }}>
                        <button
                          type="button"
                          className="icon-button info small"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleUnarchiveActionItem(item.id)
                          }}
                        >
                          ↺
                        </button>
                        <button
                          className="danger-button small"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleDeleteActionItem(item.id)
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          ) : (
            <RecapTileGrid
              items={items}
              onSelect={handleSelect}
              selectable
              selectedIds={selectedIds}
              onToggleSelect={handleToggleSelect}
            />
          )
        ) : (
          <EmptyState description="No archived items yet. Archives you send will show here." tags={[]} />
        )}
      </div>
    </>
  )
}

export default ArchivesView
